import Locale from '@/utils/locale';

// localStorage keys
export const HERO_NAME = 'HERO_NAME';

export const LEVEL_DATA = 'LEVEL_DATA';

/**
 * Event object to fire when need to close loading modal in App.vue
 * ProducedBy: views/Game.vue
 * ConsumedBy: App.vue
 */
export class CloseLoadingModalEvent {}

/**
 * Event object to fire when need to open settings modal in App.vue
 * ProducedBy: components/TopPanel.vue
 * ConsumedBy: App.vue
 */
export class OpenSettingsModalEvent {}

/**
 * Event object to fire when need to open sections modal in App.vue
 * ProducedBy: components/HelpPanel.vue
 * ConsumedBy: App.vue
 */
export class OpenSectionsModalEvent {}

/**
 * Event object to fire when we need to open powers modal in App.vue
 * ProducedBy: components/HelpPanel.vue
 * ConsumedBy: App.vue
 */
export class OpenPowersModalEvent {}

/**
 * Event object to fire when all assets load and ready to start Phaser context
 * ProducedBy: game/scenes/preload.js
 * ConsumedBy: views/Game.vue
 */
export class GameAppStartEvent {}

/**
 * Event object to fire when volume settings changed
 * ProducedBy: components/modal/SettingsModal.vue
 * ConsumedBy: game/scenes/main.js
 */
export class VolumeSettingsChangedEvent {
  constructor(music, volume) {
    this.music = music / 400;
    this.volume = volume / 100;
  }
}

/**
 * Event object to fire when workspace settings changed
 * ProducedBy: components/modal/SettingsModal.vue
 * ConsumedBy: components/Workspace.vue
 */
export class GameSettingsChangedEvent {
  constructor(blocks, language) {
    this.blocks = blocks;
    this.language = language;
  }
}

/**
 * Event object to fire when game speed setting change
 * ProducedBy: components/modal/SettingsModal.vue
 * ConsumedBy: src/game/scenes/main
 */
export class GameSpeedChangeEvent {
  constructor(speed) {
    this.speed = speed;
  }
}

/**
 * Event object to change when workspace game mode (Block, Python or Javascript)
 * ProducedBy: components/Tutorial.vue
 * ConsumedBy: components/Workspace.vue
 */
export class GameModeChangeEvent {
  constructor(mode = 'block') {
    this.mode = mode;
  }
}

/**
 * Event object to fire when raw section data processed and ready to use
 * ProducedBy: game/scenes/preload.js
 * ConsumedBy: components/GamePanel.vue
 */
export class LevelDataLoadEvent {
  constructor(game, data, challenge) {
    this.game = game;
    this.data = data;
    this.challenge = challenge;
  }
}

/**
 * Event object to fire when user state data loaded from server
 * ProducedBy: services/remote-service.js
 * ConsumedBy: components/modals/SettingsModal.vue
 */
export class UserStateLoadEvent {
  constructor() {
    // retrieve user state from remote-service after load
  }
}

/**
 * Event object to fire when we try to run the game
 * ProducedBy: components/TopPanel.vue, components/CodePanel.vue
 * ConsumedBy: components/GamePanel.vue
 */
export class TryRunEvent {}

/**
 * Event object to fire when run the game after code validation
 * ProducedBy: components/GamePanel.vue
 * ConsumedBy: game/scenes/main.js
 */
export class RunCodeEvent {}

/**
 * Event object to fire when the code successfully started running
 * ProducedBy: services/code-execution-service
 * ConsumedBy: components/TopPanel.vue
 */
export class CodeIsRunningEvent {}

/**
 * Event object to fire when we want to stop code execution
 * ProducedBy: game/scenes/main.js
 * ConsumedBy: components/GamePanel.vue
 */
export class TerminateExecutionEvent {}

/**
 * Event fired when one of heroes our of scene
 * ProducedBy: game/scenes/main.js
 * ConsumedBy: components/GamePanel.vue
 */
export class OutOfSceneEvent {}

/**
 * Event object to fire when code terminated.
 * ProducedBy: services/code-execution-service.js
 * ConsumedBy: components/GamePanel.vue
 *
 * @param {boolean} success  Make it false if we want to interrupt game success checks.
 *                           Otherwise game success expression exceptions overrides in game events like hero blocked, destroyed, etc.
 */
export class TerminateResultEvent {
  constructor(success, message = '') {
    this.success = success;
    this.message = Locale.getMessage(message);
  }
}

/**
 * Event object to fire when need to highlight a line
 * ProducedBy: services/code-execution-service
 * ConsumedBy: components/BlockPanel.vue
 */
export class HighlightEvent {
  constructor(line) {
    this.line = line;
  }
}

/**
 * Event object to fire an error occurs in game success check
 * ProducedBy: services/game-service.js
 * ConsumedBy: components/GamePanel.vue
 */
export class GameSuccessErrorEvent {
  constructor(message) {
    this.message = message;
  }
}

/**
 * Event object to fire an error occurs in game required check
 * ProducedBy: services/game-service.js
 * ConsumedBy: components/GamePanel.vue
 */
export class GameRequiredErrorEvent {
  constructor(message) {
    this.message = message;
  }
}

/**
 * Event object to fire when a challenge successfully finished
 * ProducedBy: components/GamePanel.vue
 * ConsumedBy: game/scenes/main.js
 */
export class NextChallengeEvent {
  constructor(challenge) {
    this.challenge = challenge;
  }
}

/**
 * Event object to fire when we need to restart the scene like character falls, goes out of scene, etc.
 * ProducedBy: game/scenes/main.js
 * ConsumedBy: components/GamePanel.vue
 */
export class RestartSceneEvent {}

/**
 * Event object to fire when a solution saved successfully
 * ProducedBy: components/GamePanel.vue
 * ConsumedBy: components/modals/SectionsModal.vue
 */
export class UpdateSectionModalEvent {
  constructor(challenge, stars) {
    this.challenge = challenge;
    this.stars = stars;
  }
}

/**
 * Event object to fire when we need show message panel
 * ProducedBy: components/GamePanel.vue
 * ConsumedBy: components/MessagePanel.vue
 *
 * @param {boolean} success  if true panel shows the next button
 * @param {boolean} info     if true it means an info message, just show ok button
 * @param {string}  message  text to show in panel
 * @param {number}  stars    number of stars if the challenge completed successfully. Otherwise set 0
 */
export class OpenMessagePanelEvent {
  constructor(success, info, message, stars = 0) {
    this.success = success;
    this.info = info;
    this.message = message;
    this.stars = stars;
  }
}

/**
 * A composition of OpenMessagePanelEvent to fire when we need to show scene info
 * ProducedBy: components/HelpPanel.vue, game/scenes/main.js
 * ConsumedBy: components/MessagePanel.vue
 */
export class ShowSceneInfoEvent {}

/**
 * Event object to fire when we need to hide message panel from outside
 * ProducedBy: components/TopPanel.vue
 * ConsumedBy: components/MessagePanel.vue
 */
export class HideMessagePanelEvent {}

/**
 * Event object to fire when we need to lock code execution
 * ProducedBy: components/GamePanel.vue
 * ConsumedBy: components/TopPanel.vue
 *
 * @param {boolean}  locked    true to lock code execution, default is false
 */
export class CodeExecutionLockEvent {
  constructor(locked = false) {
    this.locked = locked;
  }
}

/**
 * Event object to fire when our hero first created
 * ProducedBy: src/game/scenes/main.js
 * ConsumedBy: components/MessagePanel.vue
 */
export class HeroCreatedEvent {
  constructor(name) {
    this.name = name;
  }
}

/**
 * Event object to fire when our hero pass the challenge successfully
 * ProducedBy: components/GamePanel.vue
 * ConsumedBy: game/scenes/main.js
 */
export class HeroSuccessEvent {}

/**
 * Event object to fire when our hero say something
 * ProducedBy: game/model/hero.js
 * ConsumedBy: game/scenes/main.js, services/module-execution-service.js(if used locate() fn)
 */
export class HeroSayEvent {
  constructor(message) {
    this.message = message;
  }
}

/**
 * Event object to fire when a section completed
 * ProducedBy: components/MessagePanel.vue
 * ConsumedBy: game/scenes/main.js
 */
export class SectionCompletedEvent {
  constructor(message, rating) {
    this.message = message;
    this.rating = rating;
  }
}

/**
 * Event object to fire when want to play an audio by using phaser context
 * ProducedBy: components/MessagePanel.vue
 * ConsumedBy: game/scenes/main.js
 *
 * @param {string}  key  name of the sound
 * @param {boolean} stop set true to stop an already playing sound
 */
export class PlayAudioEvent {
  constructor(key, stop = false) {
    this.key = key;
    this.stop = stop;
  }
}

/**
 * Event object to fire when we want to TimerClock component on page
 * ProducedBy: game/model/hero.js
 * ConsumedBy: views/Game.vue
 *
 * @param {number} time time in seconds
 */
export class ShowTimerClockEvent {
  constructor(time) {
    this.time = time;
  }
}

/**
 * Event object to fire when we want to start tutorial
 * ProducedBy: components/HelpPanel.vue
 * ConsumedBy: views/Game.vue
 *
 * @param {number} time time in seconds
 */
export class StartTutorialEvent {}

/**
 * Event object to fire when user starts a new Tutorial
 * ProducedBy: components/GamePanel.vue
 * ConsumedBy: components/modals/StudioWelcomeModal.vue
 */
export class WelcomeNewTutorialEvent {}

export class ExitDialogModalEvent {
  constructor(callback = () => null) {
    this.callback = callback;
  }
}

/**
 * Event object to fire when wants to change the BG Sound
 * ProducedBy: components/modals/SettingsModal.vue
 * ConsumedBy: game/soundManager.js
 */
export class ChangeBackgroundSoundEvent {
  constructor(sound) {
    this.sound = sound;
  }
}

export class EventTimeFinished {}

export class CompetitionSubmitted {}

export class SubmitCompetitionClicked {
  constructor(callback = () => null) {
    this.callback = callback;
  }
}

export class StudioEventRedirectModalEvent {}
